import { useState } from 'react';
import { DangerButton, SecondaryButton } from '../../../components/buttons';
import { useAsyncAction } from '../../../hooks/use-async-action';
import type { ReceivingService } from '../../../services/receiving';
import { StepHeading } from './step-heading';

type WriteFailedProps = {
  storeId: string;
  sessionId: string;
  service: ReceivingService;
  readOnly: boolean;
};

/**
 * Write-failure screen (spec §10 — failed_from=sending_to_lpos).
 *
 * Shown after the write to TEMP_RCV.DBF failed and the auto-retry (×3)
 * was exhausted. Offers Retry (re-push the file) and Abandon (self-service
 * escape — clears the file if we authored it, warns the user to check LPOS).
 *
 * No Retry re-enters sending_to_lpos; No Cancel (post-Confirm, Invariant 5).
 */
export function WriteFailed({ storeId, sessionId, service, readOnly }: WriteFailedProps) {
  const [showAbandonWarning, setShowAbandonWarning] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { pending: retrying, run: handleRetry } = useAsyncAction(
    () => service.retry(storeId, sessionId),
    { errorLabel: 'receiving: retry failed', setError: setActionError, disabled: readOnly }
  );

  const { pending: abandoning, run: handleAbandon } = useAsyncAction(
    () => service.abandon(storeId, sessionId),
    {
      errorLabel: 'receiving: abandon failed',
      // A real failure clears the "may already have imported" note (a failed
      // Abandon performed nothing); the null (start) clear must leave it alone.
      setError: (msg) => {
        if (msg !== null) {
          setShowAbandonWarning(false);
        }

        setActionError(msg);
      },
      disabled: readOnly,
      // Only after the request succeeded — a failed Abandon performed nothing,
      // so showing "may already have imported" there would be misleading.
      onSuccess: () => setShowAbandonWarning(true),
    }
  );

  return (
    <div className="flex flex-col gap-4" data-testid="write-failed">
      <StepHeading>Something went wrong sending to LiquorPOS.</StepHeading>
      <p className="text-base leading-relaxed text-gray-700">
        The receiving file could not be written to the store computer after several attempts.
      </p>
      {showAbandonWarning && (
        <p className="text-sm font-medium text-amber-800 bg-amber-50 rounded-md px-3 py-2">
          LiquorPOS may already have imported this. Check LiquorPOS before starting over.
        </p>
      )}
      {actionError && (
        <p className="text-sm font-medium text-red-700" data-testid="write-failed-error">
          {actionError}
        </p>
      )}
      <div className="flex items-center justify-end gap-3 mt-2">
        <SecondaryButton onClick={handleRetry} disabled={readOnly || retrying || abandoning}>
          {retrying ? 'Retrying…' : 'Retry'}
        </SecondaryButton>
        <DangerButton onClick={handleAbandon} disabled={readOnly || retrying || abandoning}>
          {abandoning ? 'Abandoning…' : 'Abandon'}
        </DangerButton>
      </div>
    </div>
  );
}
